import { useCallback, useMemo, useState } from "react";
import { IPlaceSelection } from "./usePlaceAddress";

interface UseCtaErrorProps {
   isLocationSelected: boolean;
   isValueEmpty: boolean;
   isLocationValid: boolean;
   isCountrySupported: boolean;
   placeSelection: IPlaceSelection | undefined;
}

interface UseCtaErrorReturn {
   errorMessage?: string;
   hintMessage?: string;
   checkValidation: () => boolean;
}

export const useCtaError = ({
   isLocationSelected,
   isValueEmpty,
   isLocationValid,
   isCountrySupported,
   placeSelection
}: UseCtaErrorProps): UseCtaErrorReturn => {
   const [isDirty, setIsDirty] = useState(false);

   const address = placeSelection?.details?.addressParts;

   const getErrorMessage = useCallback(() => {
      if (!isLocationSelected && isValueEmpty && !isLocationValid) {
         return "Please enter correct full street address";
      }
      if (!isLocationSelected && !isLocationValid) {
         return "Please select your full street address from list";
      }
      if (isLocationSelected && !isLocationValid && isCountrySupported) {
         return "We're unable to fully parse your location information from the address provided. Please contact support@liquidspace.com for more information.";
      }
      return undefined;
   }, [isLocationSelected, isValueEmpty, isLocationValid, isCountrySupported]);

   const getHintMessage = useCallback(() => {
      if (isLocationSelected && !isCountrySupported && address?.country) {
         const countryName = address?.country.longText;
         return `We love ${countryName}, but we are not there yet. Join our waitlist!`;
      }
      return undefined;
   }, [isLocationSelected, isCountrySupported, address])

   const checkValidation = useCallback(() => {
      setIsDirty(true);

      const isError = getErrorMessage();

      return !isError;
   }, [getErrorMessage]);

   const errorMessage = useMemo(() => {
     if(isDirty) {
      return getErrorMessage();
     }
      return undefined;
   }, [getErrorMessage, isDirty]);

   const hintMessage = useMemo(() => {
      if(isDirty) {
         return getHintMessage();
        }
         return undefined;
   }, [getHintMessage, isDirty]);

   return {
      errorMessage,
      hintMessage,
      checkValidation,
   };
};
